/**
 * AccountRecoveryModal
 * ──────────────────────────────────────────────────────────────────────────────
 * 3-step recovery flow triggered from LoginScreen's "Forgot password?" link.
 *
 *  Step 1: Enter username  →  lookup /recovery_index
 *  Step 2: Answer security question  →  hash + compare
 *  Step 3: Success (password reset email sent) OR error state
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, User, HelpCircle, CheckCircle2, AlertCircle, ArrowLeft, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { getRecoveryQuestion, verifySecurityAnswer } from '../services/recoveryService';
import { AuthService } from '../services/api';
import { toast } from 'sonner';
import { Dialog, DialogPortal, DialogOverlay } from './ui/dialog';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface AccountRecoveryModalProps {
    onClose: () => void;
}

type Step = 'username' | 'question' | 'success' | 'error';

interface RecoveryState {
    userId: string;
    userEmail: string;
    question: string;
}

export function AccountRecoveryModal({ onClose }: AccountRecoveryModalProps) {
    const [step, setStep] = useState<Step>('username');
    const [username, setUsername] = useState('');
    const [typedQuestion, setTypedQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [recoveryState, setRecoveryState] = useState<RecoveryState | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // ── Step 1: Username lookup ───────────────────────────────────────────────
    const handleUsernameSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username.trim()) return;
        setLoading(true);
        setErrorMsg('');
        try {
            const result = await getRecoveryQuestion(username.trim());
            if (!result) {
                setErrorMsg('No recovery questions found for this username. Please contact support.');
                setLoading(false);
                return;
            }
            setRecoveryState(result);
            setStep('question');
        } catch {
            setErrorMsg('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Verify answer ─────────────────────────────────────────────────
    const handleAnswerSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!answer || !recoveryState) return;
        setLoading(true);
        setErrorMsg('');

        try {
            // Call the new Cloud Function to verify and get a custom token
            const functions = getFunctions();
            const accountRecoveryLogin = httpsCallable(functions, 'accountRecoveryLogin');
            
            const response = await accountRecoveryLogin({ 
                // Let's pass the raw `username` from the state, not `recoveryState.userId`
                username: username.trim(),
                answer: answer
            });

            const data = response.data as { token: string };
            if (!data || !data.token) {
                throw new Error('Invalid response from server.');
            }

            // Answer correct — log them in via Custom Token
            await AuthService.loginWithCustomToken(data.token);
            setStep('success');
        } catch (err: any) {
            if (err.message === 'Incorrect answer.' || err.code === 'permission-denied') {
                setErrorMsg('Incorrect question or answer.');
            } else {
                setErrorMsg(err?.message || 'Failed to recover account. Please try again.');
            }
            // Do NOT go to error step for wrong answers, just show the error message inline
            setLoading(false);
            return;
        }
    };

    const stepIndex = step === 'username' ? 0 : step === 'question' ? 1 : 2;

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogPortal>
                <div
                    className="fixed inset-0 flex items-center justify-center p-4 overflow-hidden"
                    style={{ zIndex: 99999 }}
                >
                    <DialogOverlay 
                        className="fixed inset-0 z-0" 
                        style={{ backdropFilter: 'blur(12px)', backgroundColor: 'rgba(0,0,0,0.6)' }} 
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: 20 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                        className="relative z-10 w-full rounded-3xl overflow-hidden shadow-2xl"
                        style={{
                            background: 'linear-gradient(145deg, rgba(13,13,20,0.97) 0%, rgba(8,8,12,0.99) 100%)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0 32px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
                            width: '420px',
                            maxWidth: 'calc(100vw - 32px)',
                        }}
                    >
                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-3">
                    <div className="flex items-center gap-2">
                        {step === 'question' && (
                            <button
                                onClick={() => { setStep('username'); setErrorMsg(''); setAnswer(''); }}
                                className="text-zinc-400 hover:text-white transition-colors mr-1"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                        )}
                        <h2 className="text-white font-bold text-xl tracking-tight">
                            {step === 'username' && 'Account Recovery'}
                            {step === 'question' && 'Security Question'}
                            {step === 'success' && 'Email Sent!'}
                            {step === 'error' && 'Something Went Wrong'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-all"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Step indicator */}
                {(step === 'username' || step === 'question') && (
                    <div className="flex items-center gap-2 px-5 pb-4">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-1 rounded-full transition-all duration-300"
                                style={{
                                    flex: i === stepIndex ? 2 : 1,
                                    background: i <= stepIndex
                                        ? 'linear-gradient(90deg, #4478ea, #2d1ef1)'
                                        : 'rgba(255,255,255,0.1)',
                                }}
                            />
                        ))}
                    </div>
                )}

                {/* Content */}
                <div className="px-7 pb-8">
                    <AnimatePresence mode="wait">
                        {/* ── STEP 1: Username ── */}
                        {step === 'username' && (
                            <motion.div
                                key="username"
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.22 }}
                            >
                                <p className="text-zinc-400 text-sm mb-6 leading-relaxed py-2">
                                    Enter your username and we'll ask you a security question to verify your identity.
                                </p>
                                <form onSubmit={handleUsernameSubmit} className="space-y-4">
                                    <div className="relative">
                                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                        <Input
                                            type="text"
                                            placeholder="Username"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            disabled={loading}
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            className="pl-10 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-amber-500/50"
                                        />
                                    </div>
                                    {errorMsg && (
                                        <motion.p
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="text-red-400 text-xs flex items-start gap-1.5 bg-red-500/10 p-3 rounded-xl"
                                        >
                                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                            {errorMsg}
                                        </motion.p>
                                    )}
                                    <Button
                                        type="submit"
                                        disabled={loading || !username.trim()}
                                        className="w-full h-12 rounded-xl font-bold text-white border-none shadow-lg shadow-blue-500/20"
                                        style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                    >
                                        {loading ? 'Looking up...' : 'Continue'}
                                    </Button>
                                </form>
                            </motion.div>
                        )}

                        {/* ── STEP 2: Security question ── */}
                        {step === 'question' && recoveryState && (
                            <motion.div
                                key="question"
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.22 }}
                            >
                                <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
                                    Answer your security question to recover your account.
                                </p>
                                <form onSubmit={handleAnswerSubmit} className="space-y-4">
                                    {/* Question shown to the user — they only need to remember the answer. */}
                                    <div
                                        className="rounded-xl px-4 py-3"
                                        style={{ background: 'rgba(68,120,234,0.08)', border: '1px solid rgba(68,120,234,0.20)' }}
                                    >
                                        <p className="text-[11px] uppercase tracking-wide font-bold text-[#4478ea] mb-1">
                                            Your security question
                                        </p>
                                        <p className="text-white text-sm leading-snug">
                                            {recoveryState.question}
                                        </p>
                                    </div>

                                    <Input
                                        type="text"
                                        placeholder="Your answer (case-sensitive)"
                                        value={answer}
                                        onChange={(e) => setAnswer(e.target.value)}
                                        disabled={loading}
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        autoFocus
                                        className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-amber-500/50"
                                    />
                                    <p className="text-zinc-500 text-[11px] leading-relaxed">
                                        ⚠️ The answer is <span className="text-zinc-300 font-semibold">case-sensitive</span>. Enter exactly as you typed during setup.
                                    </p>
                                    {errorMsg && (
                                        <motion.p
                                            initial={{ opacity: 0, y: -4 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="text-red-400 text-xs flex items-start gap-1.5 bg-red-500/10 p-3 rounded-xl"
                                        >
                                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                            {errorMsg}
                                        </motion.p>
                                    )}
                                    <Button
                                        type="submit"
                                        disabled={loading || !answer.trim()}
                                        className="w-full h-12 rounded-xl font-bold text-white border-none flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20"
                                        style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                    >
                                        <Send className="w-4 h-4" />
                                        {loading ? 'Verifying...' : 'Recover Account'}
                                    </Button>
                                </form>
                            </motion.div>
                        )}

                        {/* ── STEP 3: Success ── */}
                        {step === 'success' && (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ type: 'spring', stiffness: 280, damping: 24 }}
                                className="flex flex-col items-center text-center pt-2 pb-2"
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                                    style={{ background: 'linear-gradient(135deg, #22c55e22, #16a34a33)' }}
                                >
                                    <CheckCircle2 className="w-12 h-12 text-[#4478ea]" />
                                </motion.div>
                                <h2 className="text-white font-bold text-xl tracking-tight mb-2">Recovery Successful!</h2>
                                <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                                    You have been securely logged back into your account. Please go to <span className="text-white font-semibold">Settings → Security & Account</span> to create a new password.
                                </p>
                                <Button
                                    onClick={onClose}
                                    className="w-full h-12 rounded-xl font-bold text-white border-none shadow-lg shadow-blue-500/20"
                                    style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                >
                                    Continue to App
                                </Button>
                            </motion.div>
                        )}

                        {/* ── STEP: Error ── */}
                        {step === 'error' && (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center text-center pt-2 pb-2"
                            >
                                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-red-500/10">
                                    <AlertCircle className="w-8 h-8 text-red-400" />
                                </div>
                                <h3 className="text-white font-bold text-base mb-2">Recovery Failed</h3>
                                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{errorMsg}</p>
                                <div className="flex gap-3 w-full">
                                    <Button
                                        variant="outline"
                                        onClick={() => { setStep('username'); setErrorMsg(''); setAnswer(''); setRecoveryState(null); setUsername(''); }}
                                        className="flex-1 h-12 rounded-xl border-white/10 text-zinc-300"
                                    >
                                        Try Again
                                    </Button>
                                    <Button
                                        onClick={onClose}
                                        className="flex-1 h-12 rounded-xl font-bold text-white border-none shadow-lg shadow-blue-500/20"
                                        style={{ background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
